import { Predict, getStateCallbacks } from "@colyseus/sdk";
import {
  ARENA_HEIGHT,
  ARENA_WIDTH,
  ATTACK_SWING_MS,
  BODY_FIELDS,
  INTERPOLATION_DELAY_MS,
  PLAYER_HEIGHT,
  TICK_MS,
  type ArenaState,
  type FightInput,
  type MatchPhase,
  type Player,
  getMap,
  setActiveMap,
  stepBody,
} from "@stickstakes/shared";
import { createHud } from "./hud.js";
import { createInput } from "./input.js";
import { createLanding } from "./landing.js";
import { createLobbyPanel } from "./lobby.js";
import { createPauseMenu } from "./menu.js";
import { createRenderer } from "./render.js";
import { createResultPanel, shareText } from "./result.js";
import { share } from "./share.js";
import { createArena, describeJoinError, joinArenaByCode, type ArenaRoom } from "./net.js";
import { createFullscreen, createWakeLock, registerServiceWorker } from "./pwa.js";
import { createAudio } from "./audio.js";
import { createFx } from "./fx.js";
import { haptics } from "./haptics.js";

// Installable, and instant on a repeat launch from the home screen.
registerServiceWorker();
const wake = createWakeLock();
const fullscreen = createFullscreen();

const audio = createAudio();
const fx = createFx(audio);

/**
 * Browsers refuse to start an AudioContext before a real gesture, so the first
 * touch or key anywhere is what brings sound to life. `once` per event type is
 * enough — after that the context exists and `play()` resumes it if needed.
 */
for (const event of ["pointerdown", "keydown"] as const) {
  window.addEventListener(event, () => audio.unlock(), { once: true });
}

// Floating devtools on the phone. Dev builds only — this is the single biggest
// quality-of-life win for debugging with your thumbs instead of a laptop.
if (import.meta.env.DEV) {
  void import("eruda").then((m) => m.default.init());
}

const canvas = document.querySelector<HTMLCanvasElement>("#stage")!;
const statusEl = document.querySelector<HTMLElement>("#status")!;
const fullscreenBtn = document.querySelector<HTMLButtonElement>("#fullscreen")!;
const muteBtn = document.querySelector<HTMLButtonElement>("#mute")!;
const rotateEl = document.querySelector<HTMLElement>("#rotate")!;

/**
 * The fight is a 16:9 landscape arena — on a portrait phone it would letterbox
 * down to a useless sliver. So while a round is live and the phone is upright,
 * take the screen over with a "turn sideways" prompt. The menus (landing,
 * lobby, result) stay usable in either orientation.
 */
const portraitMq = window.matchMedia("(orientation: portrait)");
let fightingNow = false;
let rotateShown = false;

function updateRotateGate(): void {
  const show = fightingNow && portraitMq.matches;
  if (show === rotateShown) return;
  rotateShown = show;
  rotateEl.hidden = !show;
}

portraitMq.addEventListener("change", updateRotateGate);

function paintMuteButton(): void {
  muteBtn.textContent = audio.muted ? "🔇" : "🔊";
  muteBtn.setAttribute("aria-pressed", String(audio.muted));
}

muteBtn.addEventListener("click", () => {
  audio.unlock(); // the tap that mutes is also a perfectly good gesture to start on
  audio.toggleMute();
  paintMuteButton();
});

// Reflect the remembered choice on load, before anything can be heard.
paintMuteButton();

const renderer = createRenderer(canvas);
const input = createInput(canvas);
const hud = createHud();
const landing = createLanding();
const lobby = createLobbyPanel();
const result = createResultPanel();
const menu = createPauseMenu();

function relayout(): void {
  renderer.resize();
  input.layout(renderer.cssWidth, renderer.cssHeight);
}

relayout();
window.addEventListener("resize", relayout);
window.addEventListener("orientationchange", relayout);

/**
 * Fullscreen, if this browser has any. Where it doesn't — an iPhone, or an
 * already-fullscreen installed PWA — the button goes away rather than sitting
 * there doing nothing when pressed.
 */
function paintFullscreenButton(): void {
  const on = fullscreen.active;
  fullscreenBtn.textContent = on ? "⤡" : "⛶";
  fullscreenBtn.setAttribute("aria-pressed", String(on));
  fullscreenBtn.setAttribute("aria-label", on ? "Leave fullscreen" : "Fullscreen");
}

fullscreenBtn.hidden = !fullscreen.supported;
paintFullscreenButton();
fullscreen.onChange(() => {
  paintFullscreenButton();
  // Entering or leaving changes the viewport without always firing `resize`
  // first, and a canvas sized to the old box is a letterboxed arena.
  relayout();
});
fullscreenBtn.addEventListener("click", () => fullscreen.toggle());

/** The link that gets someone straight into this room. */
function inviteUrl(code: string): string {
  return `${location.origin}${location.pathname}?code=${code}`;
}

const clamp = (value: number, lo: number, hi: number): number =>
  value < lo ? lo : value > hi ? hi : value;

/**
 * Should this stickman be on screen at all? Dead between respawns, knocked out
 * for the round, or sitting out a match they joined late — all invisible.
 */
function isVisible(player: Player, state: ArenaState): boolean {
  const phase = state.phase as MatchPhase;
  if (phase === "lobby") return true;
  if (player.spectating) return false;
  if (player.deadUntilTick > 0) return false;
  // Knocked out: hidden for the rest of the round, back at the next countdown.
  if (player.lives === 0 && (phase === "playing" || phase === "roundOver")) return false;
  return true;
}

/** 0..1 through the attack swing, or 0 when not swinging. */
function swingProgress(player: Player, tick: number): number {
  const remaining = player.attackUntilTick - tick;
  if (remaining <= 0) return 0;
  const total = ATTACK_SWING_MS / TICK_MS;
  return Math.min(1, Math.max(0, 1 - remaining / total));
}

/**
 * Landing screen → a joined room. Loops until something works.
 *
 * `notice` is how a finished session explains itself — "you left", "the
 * connection dropped" — so the player lands back on the front screen already
 * knowing why they are looking at it.
 */
async function connect(notice = ""): Promise<ArenaRoom> {
  if (notice) landing.reject(notice);
  for (;;) {
    const choice = await landing.choose();
    try {
      const wardrobe = { color: choice.color, hat: choice.hat };
      const room = choice.code
        ? await joinArenaByCode(choice.code, choice.name, wardrobe)
        : await createArena(choice.name, wardrobe);

      // Put the code in the address bar so the host can just share the URL,
      // and so a reload rejoins the same game instead of starting a new one.
      history.replaceState(null, "", `?code=${room.roomId}`);
      landing.hide();
      return room;
    } catch (error) {
      landing.reject(
        choice.code
          ? describeJoinError(error, choice.code)
          : `Couldn't create a game. ${error instanceof Error ? error.message : error}`,
      );
    }
  }
}

/**
 * One game, start to finish. Resolves when the room is left — deliberately
 * from the menu, or because the connection went away — with a line to show on
 * the landing screen, or "" if leaving was the player's own idea.
 *
 * Everything in here belongs to this room: the reconciler, the state
 * listeners, the frame loop. Nothing survives into the next session, which is
 * what makes "leave and start another" work at all.
 */
async function runSession(room: ArenaRoom): Promise<string> {
  /** Flipped false exactly once, by `endSession()`. Stops the frame loop. */
  let live = true;
  /** What the landing screen should say about how this ended. */
  let notice = "";
  let settleEnd: () => void;
  const ended = new Promise<void>((resolve) => {
    settleEnd = resolve;
  });

  function endSession(): void {
    if (!live) return;
    live = false;
    // Out of the game — let the phone sleep like a phone again, and never
    // leave a vibration pattern running after the connection drops.
    wake.release();
    haptics.stop();
    fx.clear();
    // A thumb that was down when the game ended must not be down when the
    // next one starts.
    input.setEnabled(false);
    menu.hide();
    lobby.hide();
    result.hide();
    fightingNow = false;
    updateRotateGate();
    settleEnd();
  }

  // In a game now: keep the screen lit. Requesting it after the landing screen
  // means it always follows a real tap, which is what browsers want to see.
  wake.acquire();
  input.setEnabled(false); // the frame loop turns it on once a round is live

  /**
   * The input channel. `reliable` is right for WebSocket: every frame arrives
   * once and in order, so the server's ack (`handle.lastProcessed`) tracks our
   * sends 1:1 and the reconciler knows exactly what is still in flight.
   */
  const inputHandle = room.input<FightInput>({ mode: "reliable" });

  /**
   * Remote stickmen are drawn ~100ms behind the newest snapshot, interpolated
   * between the two straddling it — smooth motion at the cost of a little lag,
   * which nobody notices on someone else's character.
   */
  const predict = Predict.get(room, { mode: "lerp", delay: INTERPOLATION_DELAY_MS });
  predict.attachAll("players", { x: "lerp", y: "lerp" });

  /**
   * Our own stickman is different: it must answer the thumb instantly. The
   * reconciler applies each input locally the moment we send it, and when the
   * server's truth arrives it rewinds to that truth and replays whatever is
   * still unacked — using the very same `stepBody` the server just ran.
   *
   * `frozen` and `stunned` are mirrored fields, so countdowns, deaths and
   * knockbacks all replay in lock-step with the server instead of fighting it.
   */
  const $ = getStateCallbacks(room);

  /**
   * The world both sides step against. Set it before the reconciler runs, and
   * follow the host's lobby pick — the map only ever changes between rounds, so
   * flipping the shared active map here can't desync a live replay.
   */
  setActiveMap(room.state.mapId);
  $(room.state).listen("mapId", (value) => setActiveMap(value as string));

  /**
   * Landing dust needs the speed you were falling at, but by the time
   * `grounded` flips true the server has already zeroed `vy`. So keep the last
   * airborne velocity per player and read it back on touchdown.
   */
  const lastFallSpeed = new Map<string, number>();

  /**
   * Feedback is driven off decoded state rather than off our own inputs, so it
   * fires for every fighter and only for things that really happened on the
   * server. A local guess would flash on hits that never landed.
   */
  $(room.state).players.onAdd((player: Player, sessionId: string) => {
    const mine = () => sessionId === room.sessionId;
    const chest = () => player.y - PLAYER_HEIGHT * 0.55;

    $(player).listen("damage", (value, previous) => {
      // Fires on the initial sync and on the reset to 0 at respawn too; only a
      // genuine increase is a hit.
      if (previous === undefined || value <= previous) return;
      fx.hit(player.x, chest(), Math.sign(player.vx) || player.facing, value, player.color, mine());
    });

    $(player).listen("lives", (value, previous) => {
      if (previous === undefined || value >= previous) return;
      fx.death(player.x, chest(), player.color, mine());
    });

    $(player).listen("attackUntilTick", (value, previous) => {
      // 0 means the swing ended; a new non-zero tick means a fresh swing.
      if (!value || value === previous) return;
      fx.swing();
    });

    $(player).listen("jumping", (value, previous) => {
      if (value && !previous) fx.jump();
    });

    $(player).listen("grounded", (value, previous) => {
      if (!value || previous) return;
      fx.land(player.x, player.y, lastFallSpeed.get(sessionId) ?? 0);
      lastFallSpeed.set(sessionId, 0);
    });

    $(player).listen("vy", (value) => {
      if (value > 0) lastFallSpeed.set(sessionId, value);
    });
  });

  $(room.state).players.onRemove((_player: Player, sessionId: string) => {
    lastFallSpeed.delete(sessionId);
  });

  /** Round and match transitions: the punctuation of the whole thing. */
  $(room.state).listen("phase", (value, previous) => {
    if (previous === undefined || value === previous) return;
    const won = room.state.matchWinnerId === room.sessionId;
    if (value === "playing") {
      fx.clear(); // no sparks from last round hanging over this one
      fx.roundStart();
    } else if (value === "roundOver") {
      fx.roundEnd(won);
    } else if (value === "matchOver") {
      fx.matchOver(won);
    }
  });

  $(room.state).players.onAdd((player: Player, sessionId: string) => {
    if (sessionId !== room.sessionId) return;
    predict.reconciler(player, {
      input: inputHandle,
      fields: BODY_FIELDS,
      step: (ctx, body, command) => {
        // Identical code path to the server's fixed step. If these two ever
        // disagree, you get rubber-banding — which is why it lives in /shared.
        // Falling off is *not* handled here: death costs a life, and only the
        // server gets to decide that.
        stepBody(body, command, ctx.dt);
      },
    });
  });

  lobby.onStart(() => room.send("startMatch"));
  result.onPlayAgain(() => room.send("startMatch"));

  /**
   * Out of the room and back to the landing screen. Their own decision, so no
   * notice waiting for them there — and the session ends now rather than when
   * the socket does, so the screen changes the instant they press it.
   */
  function leaveGame(): void {
    endSession();
    void room.leave();
  }

  menu.on({
    restart: () => room.send("restartMatch"),
    toLobby: () => room.send("endMatch"),
    setPaused: (paused) => room.send("pause", { paused }),
    leave: leaveGame,
  });
  lobby.onLeave(leaveGame);
  lobby.onConfigure((change) => room.send("configure", change));
  lobby.onCustomize((change) => room.send("customize", change));
  lobby.onReady((ready) => room.send("ready", { ready }));

  function flash(message: string): void {
    if (!message) return;
    statusEl.dataset.flash = message;
    setTimeout(() => delete statusEl.dataset.flash, 2200);
  }

  lobby.onShare(async () => {
    flash(await share(`Join my StickStakes game — code ${room.roomId}`, inviteUrl(room.roomId)));
  });
  result.onShare(async (text) => {
    flash(await share(text, inviteUrl(room.roomId)));
  });

  if (import.meta.env.DEV) {
    Object.assign(globalThis, {
      __ss: {
        room,
        predict,
        fx,
        audio,
        feel: () => ({
          ...fx.stats,
          particles: fx.particles.length,
          shakeX: Number(fx.shakeX.toFixed(2)),
          muted: audio.muted,
        }),
        code: () => room.roomId,
        start: () => room.send("startMatch"),
        ready: (value = true) => room.send("ready", { ready: value }),
        configure: (change: unknown) => room.send("configure", change),
        phase: () => room.state.phase,
        stake: () => room.state.stake,
        share: () => shareText(room.state, room.sessionId),
        match: () => ({
          phase: room.state.phase,
          round: room.state.round,
          totalRounds: room.state.totalRounds,
          livesPerRound: room.state.livesPerRound,
          stake: room.state.stake,
          tick: room.state.tick,
          hostId: room.state.hostId,
          matchWinnerId: room.state.matchWinnerId,
        }),
        dump: () =>
          Array.from(room.state.players.entries()).map(([id, p]) => ({
            id,
            name: p.name,
            color: p.color,
            hat: p.hat,
            self: id === room.sessionId,
            x: Math.round(predict.value(p, "x")),
            y: Math.round(predict.value(p, "y")),
            lives: p.lives,
            wins: p.roundWins,
            damage: p.damage,
            stunned: p.stunned,
            frozen: p.frozen,
            dead: p.deadUntilTick > 0,
            spectating: p.spectating,
          })),
      },
    });
  }

  room.onLeave((code) => {
    // Either we asked (endSession has already run and this is a no-op), or the
    // connection went away under us — in which case say so on the way out.
    if (live) notice = `Connection lost (${code}). Pick a game to get back in.`;
    statusEl.textContent = `disconnected (${code})`;
    endSession();
  });

  let debugAt = 0;
  let lastFrame = performance.now();

  function frame(now: number): void {
    if (!live) return; // the room is gone; so is its loop
    const state = room.state;
    const phase = state.phase as MatchPhase;

    // Real elapsed seconds, clamped: a backgrounded tab comes back with a huge
    // delta, and letting that reach the particles would teleport them.
    const dt = Math.min(0.05, Math.max(0, (now - lastFrame) / 1000));
    lastFrame = now;
    fx.update(dt);

    // The thumb controls answer only when there is a round to drive and
    // nothing on top of it. Anything else — a panel, the menu, the rotate
    // takeover — and every held control is dropped rather than left latched.
    const playable = phase === "countdown" || phase === "playing";
    input.setEnabled(playable && !menu.open && !rotateShown);
    // Second line of defence, every frame: a pointer the browser quietly took
    // back is the whole reason a stickman ever walked into a wall on its own.
    input.sweep();

    // One driver for the whole prediction stack. Returns how many fixed input
    // steps are due this frame — we send exactly that many, no more.
    const steps = predict.tick(now);
    for (let i = 0; i < steps; i++) {
      inputHandle.data.left = input.intent.left;
      inputHandle.data.right = input.intent.right;
      inputHandle.data.jump = input.intent.jump;
      inputHandle.data.attack = input.intent.attack;
      inputHandle.send();
    }

    // The parallax camera trails your own fighter (or the arena centre before
    // one exists), clamped so the layers sway rather than pan.
    const selfPlayer = state.players.get(room.sessionId);
    const focusX = selfPlayer ? predict.value(selfPlayer, "x") : ARENA_WIDTH / 2;
    const focusY = selfPlayer ? predict.value(selfPlayer, "y") : ARENA_HEIGHT * 0.6;
    const cam = {
      x: clamp(focusX - ARENA_WIDTH / 2, -260, 260),
      y: clamp(focusY - ARENA_HEIGHT * 0.62, -150, 150),
      t: now / 1000,
    };
    const map = getMap(state.mapId);

    renderer.clear();
    renderer.beginWorld(fx.shakeX, fx.shakeY);
    renderer.drawWorldBack(map, cam);

    const showLives = phase !== "lobby";

    for (const [sessionId, player] of state.players) {
      if (!isVisible(player, state)) continue;

      renderer.drawStickman({
        // The one read idiom: reconciled for us, interpolated for everyone else.
        x: predict.value(player, "x"),
        y: predict.value(player, "y"),
        facing: player.facing,
        grounded: player.grounded,
        color: player.color,
        hat: player.hat,
        name: player.name,
        isSelf: sessionId === room.sessionId,
        lives: player.lives,
        maxLives: state.livesPerRound,
        showLives: showLives && !player.spectating,
        swing: swingProgress(player, state.tick),
        invulnerable: player.invulnUntilTick > state.tick,
        damage: player.damage,
        stunned: player.stunned,
      });
    }

    // Sparks over the arena but under the fighters would be invisible behind
    // them at the moment of impact, so they go on top.
    renderer.drawParticles(fx.particles);

    renderer.endWorld();
    // The thumb controls only belong on screen when there is something to
    // drive — never under the lobby panel, the result card, or the menu.
    if (playable && !menu.open) {
      renderer.drawControls(input.zones, input.active, input.stick);
    }

    hud.update(state, room.sessionId);

    // Gate the "turn your phone" takeover on the live-round phases.
    fightingNow =
      phase === "countdown" || phase === "playing" || phase === "roundOver";
    updateRotateGate();

    // The lobby panel and the result card each own one phase; both stay out of
    // the way while there is a fight to watch.
    if (phase === "lobby") lobby.update(state, room.sessionId, room.roomId);
    else lobby.hide();

    if (phase === "matchOver") result.update(state, room.sessionId);
    else result.hide();

    menu.update(state, room.sessionId);

    if (now - debugAt > 250) {
      debugAt = now;
      const rtt = Math.round(room.clock.rtt());
      // Colour the connection dot: green under 80ms, amber under 160, red past.
      statusEl.dataset.ping = rtt < 80 ? "good" : rtt < 160 ? "ok" : "bad";
      statusEl.textContent =
        statusEl.dataset.flash ??
        (state.paused
          ? `${room.roomId} · paused`
          : import.meta.env.DEV
            ? `${room.roomId} · ${phase} · ${state.players.size}/${state.maxPlayers} · ` +
              `${rtt}ms · ${inputHandle.pendingCount} in flight`
            : `${room.roomId} · ${rtt}ms`);
    }

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);

  await ended;
  return notice;
}

/**
 * The app: one game after another, with the landing screen in between. Leaving
 * a room is not the end of anything — it just puts the front screen back up.
 */
async function main(): Promise<void> {
  let notice = "";
  for (;;) {
    const room = await connect(notice);
    notice = await runSession(room);
    // No game any more, so drop the code from the address bar: a reload from
    // the landing screen should offer a fresh start, not march back into the
    // room they just walked out of.
    history.replaceState(null, "", location.pathname);
  }
}

main().catch((error: unknown) => {
  console.error(error);
  landing.reject(`Something broke: ${error instanceof Error ? error.message : String(error)}`);
});
